// WebSocket service for handling real-time communication
interface Contact {
  id: string;
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
}

class WebSocketService {
  private static instance: WebSocketService;
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 1000; // Start with 1 second

  private constructor() {
    this.initializeWebSocket();
  }

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  private initializeWebSocket(): void {
    try {
      const wsUrl = process.env.REACT_APP_WS_URL || 'wss://lavernaibackend.onrender.com';
      console.log('[WebSocket] Initializing connection to:', wsUrl);
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('[WebSocket] Connection established');
        this.reconnectAttempts = 0;
        this.reconnectTimeout = 1000;
      };

      this.socket.onclose = (event) => {
        console.log('[WebSocket] Connection closed:', event.code, event.reason);
        this.handleReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };

      this.socket.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    } catch (error) {
      console.error('[WebSocket] Error initializing:', error);
      this.handleReconnect();
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.reconnectTimeout *= 2; // Exponential backoff
      setTimeout(() => this.initializeWebSocket(), this.reconnectTimeout);
    } else {
      console.error('[WebSocket] Max reconnection attempts reached');
    }
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);
      // Handle different message types
      switch (message.type) {
        case 'EMERGENCY_ALERT':
          this.handleEmergencyAlert(message.data);
          break;
        case 'EMERGENCY_RESOLVED':
          this.handleEmergencyResolved(message.data);
          break;
        default:
          console.log('[WebSocket] Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('[WebSocket] Error handling message:', error);
    }
  }

  private handleEmergencyAlert(data: any): void {
    // Dispatch event for emergency alert
    const event = new CustomEvent('emergency-alert', { detail: data });
    window.dispatchEvent(event);
  }

  private handleEmergencyResolved(data: any): void {
    // Dispatch event for emergency resolved
    const event = new CustomEvent('emergency-resolved', { detail: data });
    window.dispatchEvent(event);
  }

  public async broadcastEmergency(contacts: Contact[]): Promise<void> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    try {
      this.socket.send(JSON.stringify({
        type: 'EMERGENCY_ALERT',
        data: {
          contacts,
          timestamp: Date.now()
        }
      }));
    } catch (error) {
      console.error('[WebSocket] Error broadcasting emergency:', error);
      throw error;
    }
  }

  public async broadcastEmergencyResolved(contacts: Contact[]): Promise<void> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    try {
      this.socket.send(JSON.stringify({
        type: 'EMERGENCY_RESOLVED',
        data: {
          contacts,
          timestamp: Date.now()
        }
      }));
    } catch (error) {
      console.error('[WebSocket] Error broadcasting emergency resolved:', error);
      throw error;
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
}

export default WebSocketService; 